import type { Theme, ThemeTokens, BackgroundTokens, ColorTokens } from '@/types/theme';
import { GOOGLE_FONTS } from './google-fonts';

// ── Contrast auto-fix ─────────────────────────────────────────────────────────
// Used both as a CSS-apply safety net and as an explicit "fix" action in the builder.

/** Parse HSL string "H S% L%" → lightness 0..100, or null on failure */
function hslLightness(hsl: string): number | null {
  const parts = hsl.trim().split(/\s+/);
  if (parts.length < 3) return null;
  return parseFloat(parts[2]);
}

/** Replace just the lightness in an HSL string */
function setHslLightness(hsl: string, newL: number): string {
  const parts = hsl.trim().split(/\s+/);
  if (parts.length < 3) return hsl;
  return `${parts[0]} ${parts[1]} ${newL.toFixed(1)}%`;
}

/** WCAG relative luminance of an HSL string */
function hslRelativeLuminance(hsl: string): number | null {
  const parts = hsl.trim().split(/\s+/);
  if (parts.length < 3) return null;
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1]) / 100;
  const l = parseFloat(parts[2]) / 100;
  const a = s * Math.min(l, 1 - l);
  const linearCh = (n: number) => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linearCh(0) + 0.7152 * linearCh(8) + 0.0722 * linearCh(4);
}

function contrastRatio(hsl1: string, hsl2: string): number {
  const l1 = hslRelativeLuminance(hsl1);
  const l2 = hslRelativeLuminance(hsl2);
  if (l1 === null || l2 === null) return 21;
  const lighter = Math.max(l1, l2);
  const darker  = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Foreground / background token pairs to check and auto-fix.
 *  NOTE: JS inline styles set via applyThemeTokensToCss() win over the
 *  .dark { --card-foreground: ... } rules in globals.css, so EVERY
 *  foreground/background pair must be covered here or invisible text
 *  from a saved custom theme can slip through. */

/**
 * "Button" pairs — interactive element text that MUST have maximum contrast.
 * Dark bg → near-white text. Light bg → near-black text. No compromise.
 */
const BUTTON_CONTRAST_PAIRS: Array<{ fg: keyof ColorTokens; bg: keyof ColorTokens }> = [
  { fg: 'primaryForeground',        bg: 'primary' },
  { fg: 'secondaryForeground',      bg: 'secondary' },
  { fg: 'accentForeground',         bg: 'accent' },
  { fg: 'destructiveForeground',    bg: 'destructive' },
  { fg: 'sidebarPrimaryForeground', bg: 'sidebarPrimary' },
  { fg: 'sidebarAccentForeground',  bg: 'sidebarAccent' },
];

/**
 * "Body text" pairs — general readable text. Target WCAG AAA (7:1) so it's
 * pushed well past AA rather than barely passing.
 */
const BODY_CONTRAST_PAIRS: Array<{ fg: keyof ColorTokens; bg: keyof ColorTokens }> = [
  { fg: 'foreground',               bg: 'background' },
  { fg: 'foreground',               bg: 'card' },
  { fg: 'cardForeground',           bg: 'card' },
  { fg: 'popoverForeground',        bg: 'popover' },
  { fg: 'mutedForeground',          bg: 'muted' },
  { fg: 'sidebarForeground',        bg: 'sidebarBackground' },
];

/** Minimum acceptable contrast for body text (WCAG AAA) */
const BODY_TARGET_CONTRAST = 7.0;

/**
 * Return a copy of `colors` with foreground tokens adjusted for maximum readability:
 *
 * • Button / interactive tokens: pushed straight to near-black (5 %) or
 *   near-white (95 %) depending on background lightness — no in-between.
 *
 * • Body / card / sidebar tokens: binary-searched to meet WCAG AAA (7:1).
 *   Hue and saturation are preserved so the theme's colour identity stays.
 *
 * Exported so the theme builder can offer a manual "Auto-fix" button.
 */
export function fixContrastTokens(colors: ColorTokens): ColorTokens {
  const fixed = { ...colors };

  // ── 1. Button pairs — maximum contrast, push to extreme ───────────────────
  for (const { fg, bg } of BUTTON_CONTRAST_PAIRS) {
    const fgHsl = fixed[fg] as string | undefined;
    const bgHsl = fixed[bg] as string | undefined;
    if (!fgHsl || !bgHsl) continue;

    const bgL = hslLightness(bgHsl) ?? 50;
    // For interactive elements always use full opposite extreme
    // Light bg (>= 60%) → very dark text (5%); dark bg (≤ 40%) → very light text (95%)
    // Mid-range (40–60%) → use contrast check to decide direction
    let targetL: number;
    if (bgL >= 60)      targetL = 5;    // bright/light bg → black text
    else if (bgL <= 40) targetL = 95;   // dark bg → white text
    else {
      // Mid-range: pick whichever extreme gives more contrast
      const darkCandidate  = setHslLightness(fgHsl, 5);
      const lightCandidate = setHslLightness(fgHsl, 95);
      targetL = contrastRatio(darkCandidate, bgHsl) >= contrastRatio(lightCandidate, bgHsl) ? 5 : 95;
    }

    const newFg = setHslLightness(fgHsl, targetL);
    // Only apply if it actually improves contrast (safety guard)
    if (contrastRatio(newFg, bgHsl) > contrastRatio(fgHsl, bgHsl)) {
      (fixed as Record<string, string>)[fg as string] = newFg;
    }
  }

  // ── 2. Body text pairs — binary search to hit AAA ─────────────────────────
  for (const { fg, bg } of BODY_CONTRAST_PAIRS) {
    const fgHsl = fixed[fg] as string | undefined;
    const bgHsl = fixed[bg] as string | undefined;
    if (!fgHsl || !bgHsl) continue;

    if (contrastRatio(fgHsl, bgHsl) >= BODY_TARGET_CONTRAST) continue; // already fine

    const bgL = hslLightness(bgHsl) ?? 50;
    const targetL = bgL < 50 ? 95 : 5;

    // Binary-search: find lightness that hits BODY_TARGET_CONTRAST
    const fgL = hslLightness(fgHsl) ?? (bgL < 50 ? 10 : 90);
    let lo = bgL < 50 ? fgL : targetL;
    let hi = bgL < 50 ? targetL : fgL;
    if (lo > hi) [lo, hi] = [hi, lo];

    let bestL = targetL; // default to full extreme if search fails
    for (let iter = 0; iter < 22; iter++) {
      const mid = (lo + hi) / 2;
      const candidate = setHslLightness(fgHsl, mid);
      if (contrastRatio(candidate, bgHsl) >= BODY_TARGET_CONTRAST) {
        bestL = mid;
        if (bgL < 50) hi = mid; else lo = mid;
      } else {
        if (bgL < 50) lo = mid; else hi = mid;
      }
    }

    (fixed as Record<string, string>)[fg as string] = setHslLightness(fgHsl, bestL);
  }

  return fixed;
}

// Convert camelCase to kebab-case for CSS variables
function camelToKebab(str: string): string {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Remove ALL theme-related inline CSS custom properties from :root so that the
 * stylesheet-defined values in globals.css take over cleanly.
 * Call this before applying the system/plain preset to guarantee no leftover
 * custom-theme values bleed through.
 */
export function clearThemeInlineStyles(): void {
  if (typeof document === 'undefined') return;
  // Vars are being wiped — the next apply must run even if its tokens match
  // the last applied set (see _lastAppliedTokenSig in applyThemeTokensToCss).
  _lastAppliedTokenSig = '';
  const root = document.documentElement;
  // All property names that applyThemeTokensToCss might have set via setProperty:
  const COLOR_PROPS = [
    'background', 'foreground', 'header-background', 'card', 'card-foreground',
    'popover', 'popover-foreground', 'primary', 'primary-foreground',
    'player-toggle-background', 'secondary', 'secondary-foreground',
    'muted', 'muted-foreground', 'accent', 'accent-foreground',
    'destructive', 'destructive-foreground', 'border', 'input', 'ring',
    'sidebar-background', 'sidebar-foreground', 'sidebar-primary',
    'sidebar-primary-foreground', 'sidebar-accent', 'sidebar-accent-foreground',
    'sidebar-border', 'sidebar-ring',
    'chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5',
    'btn-bg', 'btn-fg', 'btn-hover-bg',
    'btn-outline-fg', 'btn-outline-hover-bg', 'btn-outline-hover-fg',
    'btn-outline-border', 'btn-outline-border-width',
    'btn-ghost-fg', 'btn-ghost-hover-bg', 'btn-ghost-hover-fg',
    'btn-selected-bg', 'btn-selected-fg',
  ];
  COLOR_PROPS.forEach(prop => root.style.removeProperty(`--${prop}`));
  // Do NOT remove --radius, --font-global, --thumbnail-radius etc. — those are
  // set by appearance settings and should survive a theme reset.
}

// Signature of the last applied token set — several boot-time callers
// (auth resolve → loadTheme, profile arrival → loadTheme again,
// customizations snapshot, prefersDark effect) frequently apply IDENTICAL
// tokens back-to-back. Each application rewrites dozens of CSS vars on the
// root → a full-page style recalculation — visible as the theme "changing
// a couple of times" at startup and pure wasted load in the fragile boot
// window on iPhone. Identical re-applies are now skipped.
// Seeded from the pre-paint inline script (window.__bbThemeSig) when it
// restored a cached theme — so the first React-side application of the SAME
// theme is skipped entirely (zero restyles on a warm boot).
let _lastAppliedTokenSig: string =
  (typeof window !== 'undefined' && (window as any).__bbThemeSig) || '';

/**
 * Sidebar-follows-theme consistency pass. Reads the CURRENT CSS vars off the
 * root and, when the sidebar's lightness is wildly off the page background
 * (>40 points — e.g. the presets' pure-black sidebar on a light theme, or a
 * stale value replayed by the pre-paint cache script), re-derives the whole
 * sidebar surface from the theme background. Refreshes the pre-paint cache
 * when it corrects anything so the fix sticks across boots.
 */
export function normalizeSidebarSurfaceVars(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const read = (n: string) =>
    (root.style.getPropertyValue(n) || getComputedStyle(root).getPropertyValue(n) || '').trim();
  const bg = read('--background');
  const sb = read('--sidebar-background');
  if (!bg || !sb) return;
  const bgL = hslLightness(bg);
  const sbL = hslLightness(sb);
  if (bgL == null || sbL == null || Math.abs(bgL - sbL) <= 40) return;

  const derived = setHslLightness(bg, bgL >= 50 ? Math.max(bgL - 5, 0) : Math.max(bgL - 2, 0));
  const fg = read('--foreground') || (bgL >= 50 ? '0 0% 0%' : '0 0% 100%');
  root.style.setProperty('--sidebar-background', derived);
  root.style.setProperty('--sidebar-foreground', fg);
  root.style.setProperty('--sidebar-accent', setHslLightness(bg, bgL >= 50 ? Math.max(bgL - 10, 0) : Math.min(bgL + 6, 100)));
  root.style.setProperty('--sidebar-accent-foreground', fg);
  const prim = read('--primary');
  if (prim) root.style.setProperty('--sidebar-primary', prim);
  const primFg = read('--primary-foreground');
  if (primFg) root.style.setProperty('--sidebar-primary-foreground', primFg);
  const border = read('--border');
  if (border) root.style.setProperty('--sidebar-border', border);

  // Refresh the pre-paint replay cache (same sig, corrected vars) so the
  // stale value can't come back on the next boot.
  try {
    const raw = localStorage.getItem('bb-theme-css-cache');
    if (raw) {
      const tc = JSON.parse(raw);
      const vars: Record<string, string> = {};
      for (let i = 0; i < root.style.length; i++) {
        const n = root.style[i];
        if (n && n.startsWith('--')) vars[n] = root.style.getPropertyValue(n);
      }
      localStorage.setItem('bb-theme-css-cache', JSON.stringify({ ...tc, vars, dark: root.classList.contains('dark') }));
    }
  } catch { /* storage unavailable — correction still applied for this session */ }
}

// Apply theme tokens to CSS custom properties
export function applyThemeTokensToCss(tokens: ThemeTokens): void {
  if (typeof document === 'undefined' || !tokens) return;

  let sig = '';
  try {
    sig = JSON.stringify(tokens);
    // Even on the skip path, keep the sidebar consistent — a stale cached
    // black-sidebar var replayed by the pre-paint script would otherwise
    // survive the whole session (the guard below never runs on skips).
    if (sig && sig === _lastAppliedTokenSig) { normalizeSidebarSurfaceVars(); return; }
  } catch { /* unserializable — apply unconditionally */ }
  _lastAppliedTokenSig = sig;

  const root = document.documentElement;

  // Safely iterate over token groups with null guard
  const applyTokenGroup = (group: Record<string, string> | undefined | null) => {
    if (!group || typeof group !== 'object') return;
    Object.entries(group).forEach(([key, value]) => {
      if (value != null) {
        const cssVarName = camelToKebab(key);
        root.style.setProperty(`--${cssVarName}`, value);
      }
    });
  };

  // ── Safety net 1: auto-fix any catastrophically low contrast ──────────────
  const safeColors = tokens.colors ? fixContrastTokens(tokens.colors) : tokens.colors;

  // Apply color tokens (using the contrast-safe version)
  applyTokenGroup(safeColors as any);
  // Lines & outlines / Menu icons (Gordon 2026-11-02: "many outline i cant
  // change to white … nor the menu icons"): outlines are drawn with Text at
  // 40–50 % and menu icons with Sidebar text at 80 %, so on a dark custom
  // background they never reach white. A theme that sets these colours gets
  // them at full strength (html[data-bb-lines] / [data-bb-icons] in
  // globals.css); a theme without them drops the stale value.
  for (const [key, attr] of [['lines', 'data-bb-lines'], ['icons', 'data-bb-icons']] as const) {
    if ((safeColors as Partial<ColorTokens> | undefined)?.[key]) root.setAttribute(attr, '');
    else { root.removeAttribute(attr); root.style.removeProperty(`--${key}`); }
  }

  // ── Safety net 2: derive button tokens from bg/fg when not explicitly set ─
  // RULE (applies to ALL themes including country/flag themes):
  //   • If background is DARK  → button border + text = pure WHITE (0 0% 100%)
  //   • If background is LIGHT → button border + text = pure BLACK (0 0% 0%)
  //   • Selected / hover state = exact invert of unselected
  //
  // We always use pure black or pure white for button borders/text — never a
  // coloured value from the theme. A flag theme may have yellow or red as its
  // "foreground" token, but that colour would be invisible against a same-hue
  // button background. Pure B&W guarantees maximum contrast in every theme.
  if (safeColors) {
    const bg = safeColors.background ?? '0 0% 0%';
    const bgL = hslLightness(bg) ?? 0;

    // Pure black or white — whichever contrasts with the background
    const pureContrast  = bgL >= 50 ? '0 0% 0%'   : '0 0% 100%'; // dark on light, white on dark
    const pureInverse   = bgL >= 50 ? '0 0% 100%' : '0 0% 0%';   // inverted for selected/hover

    // Subtle ghost hover tint: stays in the same lightness direction as the bg
    const subtleHoverL = bgL < 50
      ? Math.min(bgL + 12, 30)   // dark bg: slightly lighter
      : Math.max(bgL - 12, 70);  // light bg: slightly darker
    const subtleHover = setHslLightness(pureContrast, subtleHoverL);

    // ── Default button bg + fg: ALWAYS forced to safe values. ─────────────────
    // No theme or rotation is allowed to break the default button contrast.
    // Light bg → near-black button with near-white text.
    // Dark bg  → dark-gray button with near-white text.
    const safeBtnBg     = bgL >= 50 ? '0 0% 8%'   : '0 0% 22%';
    const safeBtnFg     = '0 0% 97%'; // near-white always readable on dark button
    const safeBtnHover  = bgL >= 50 ? '0 0% 18%'  : '0 0% 30%';
    root.style.setProperty('--btn-bg',       safeBtnBg);
    root.style.setProperty('--btn-fg',       safeBtnFg);
    root.style.setProperty('--btn-hover-bg', safeBtnHover);

    // Only override if theme hasn't explicitly set these btn tokens
    const c = safeColors as unknown as Record<string, string>;
    if (!c.btnOutlineBorder)      root.style.setProperty('--btn-outline-border',    pureContrast);
    if (!c.btnOutlineFg)          root.style.setProperty('--btn-outline-fg',         pureContrast);
    if (!c.btnOutlineHoverBg)     root.style.setProperty('--btn-outline-hover-bg',  pureContrast);
    if (!c.btnOutlineHoverFg)     root.style.setProperty('--btn-outline-hover-fg',  pureInverse);
    if (!c.btnSelectedBg)         root.style.setProperty('--btn-selected-bg',        pureContrast);
    if (!c.btnSelectedFg)         root.style.setProperty('--btn-selected-fg',        pureInverse);
    if (!c.btnGhostHoverBg)       root.style.setProperty('--btn-ghost-hover-bg',    subtleHover);
    if (!c.btnGhostHoverFg)       root.style.setProperty('--btn-ghost-hover-fg',    pureContrast);

    // ── Safety net 3: sidebar/header/player backgrounds follow the theme ────
    // applyTokenGroup only writes tokens the theme HAS. A theme without
    // sidebarBackground used to keep the STALE inline value from whatever
    // theme was applied before it — Gordon saw a black sidebar stuck on a
    // light theme ("the side menu bg color is not consistent with the
    // theme"). Derive every missing surface token from the theme's own
    // background so surfaces can never disagree with the page.
    const surfaceL = bgL >= 50 ? Math.max(bgL - 5, 0) : Math.max(bgL - 2, 0);
    const derivedSurface = setHslLightness(bg, surfaceL);
    const fgTok = safeColors.foreground ?? pureContrast;
    if (!c.sidebarBackground)        root.style.setProperty('--sidebar-background',         derivedSurface);
    if (!c.sidebarForeground)        root.style.setProperty('--sidebar-foreground',         fgTok);
    if (!c.sidebarPrimary && c.primary)            root.style.setProperty('--sidebar-primary',            c.primary);
    if (!c.sidebarPrimaryForeground && c.primaryForeground) root.style.setProperty('--sidebar-primary-foreground', c.primaryForeground);
    if (!c.sidebarAccent)            root.style.setProperty('--sidebar-accent',             setHslLightness(bg, bgL >= 50 ? Math.max(bgL - 10, 0) : Math.min(bgL + 6, 100)));
    if (!c.sidebarAccentForeground)  root.style.setProperty('--sidebar-accent-foreground',  fgTok);
    if (!c.sidebarBorder && c.border) root.style.setProperty('--sidebar-border',            c.border);
    if (!c.headerBackground)         root.style.setProperty('--header-background',          derivedSurface);
    if (!c.playerBackground)         root.style.setProperty('--player-background',          derivedSurface);
  }

  // Apply typography tokens
  applyTokenGroup(tokens.typography as any);

  // Apply spacing tokens — except the app chrome's own heights: the player bar
  // and header are set by the layout (globals.css: 50px desktop player = header
  // height, 56px phone bar — stone rules). A theme's old `playerHeight: '80px'`
  // made the footer 80px with a stretched thumbnail (Gordon 2026-11-01).
  const { playerHeight: _ph, headerHeight: _hh, ...themeSpacing } = (tokens.spacing || {}) as unknown as Record<string, string>;
  applyTokenGroup(themeSpacing as any);
  root.style.removeProperty('--player-height');
  root.style.removeProperty('--header-height');

  // Apply radius
  if (tokens.radius?.radius) {
    root.style.setProperty('--radius', tokens.radius.radius);
  }

  // Apply shadows
  applyTokenGroup(tokens.shadows as any);

  // ── Sync the Tailwind `dark` class.
  // If the user has explicitly chosen dark/light (bb-theme-mode in localStorage),
  // respect that choice unconditionally — never let background lightness override it.
  // Only fall back to lightness-detection when no explicit mode is stored (e.g. first
  // visit or themes that don't set a mode, like High Contrast Dark).
  if (typeof localStorage !== 'undefined') {
    // Visitors: no stored mode counts (every visit follows the system — isVisitorNow
    // in theme-context), so the class follows the tokens actually applied.
    const visitor = localStorage.getItem('bb-signed-in') !== '1';
    const explicitMode = visitor ? null : localStorage.getItem('bb-theme-mode');
    if (explicitMode === 'dark') {
      root.classList.add('dark');
    } else if (explicitMode === 'light') {
      root.classList.remove('dark');
    } else if (tokens.colors?.background) {
      // No explicit preference — infer from background lightness
      const bgParts = tokens.colors.background.trim().split(/\s+/);
      const lightness = parseFloat(bgParts[2] ?? '50');
      root.classList.toggle('dark', lightness < 50);
    }
  } else if (tokens.colors?.background) {
    const bgParts = tokens.colors.background.trim().split(/\s+/);
    const lightness = parseFloat(bgParts[2] ?? '50');
    root.classList.toggle('dark', lightness < 50);
  }

  // Sidebar must FOLLOW the theme — several presets explicitly ship a pure
  // black sidebar even on light themes (Gordon: "I see light theme and a
  // dark side menu???"). Run the consistency pass BEFORE persisting so the
  // pre-paint cache replays corrected values next boot.
  normalizeSidebarSurfaceVars();

  // ── Persist the applied theme for pre-paint restore ──────────────────────
  // The inline <head> script re-applies these exact CSS vars (and the dark
  // class) BEFORE first paint on the next boot, and seeds the signature so
  // the first identical React-side application is skipped. This is what
  // finally stops the logged-in boot flash: a signed-in user's theme comes
  // from Firestore (profileThemeId), which no pre-paint heuristic can know —
  // but the previous session's applied result CAN be replayed instantly.
  try {
    const vars: Record<string, string> = {};
    for (let i = 0; i < root.style.length; i++) {
      const name = root.style[i];
      if (name && name.startsWith('--')) vars[name] = root.style.getPropertyValue(name);
    }
    localStorage.setItem('bb-theme-css-cache', JSON.stringify({ sig, vars, dark: root.classList.contains('dark') }));
  } catch { /* storage unavailable — boot flash returns, nothing breaks */ }

  // Apply background media (images / videos) for each UI section
  applyBackgrounds(tokens.backgrounds);
}

// ── Apply per-section background images and videos ───────────────────────────
// Uses CSS ::before pseudo-elements for images (zero DOM mutation, React-safe).
// Uses injected <video> elements for video backgrounds (only stable fixed elements).
export function applyBackgrounds(backgrounds: BackgroundTokens | undefined | null): void {
  if (typeof document === 'undefined') return;

  // Remove previously injected CSS
  const existingStyle = document.getElementById('theme-bg-styles');
  if (existingStyle) existingStyle.remove();

  // Remove previously injected video elements
  document.querySelectorAll('[data-theme-video-bg]').forEach(el => el.remove());

  if (!backgrounds) return;

  const sections: Array<{ key: keyof BackgroundTokens; selector: string }> = [
    { key: 'body',       selector: '[data-bg="body"]' },
    { key: 'header',     selector: '[data-bg="header"]' },
    { key: 'sidebar',    selector: '[data-bg="sidebar"]' },
    { key: 'card',       selector: '[data-bg="card"]' },
    { key: 'player',     selector: '[data-bg="player"]' },
    { key: 'chatHeader', selector: '[data-bg="chat-header"]' },
    { key: 'chatBody',   selector: '[data-bg="chat-body"]' },
    { key: 'chatButton', selector: '[data-bg="chat-button"]' },
  ];

  const cssRules: string[] = [];

  sections.forEach(({ key, selector }) => {
    const bg = backgrounds[key];
    if (!bg) return;

    const opacity = bg.opacity ?? 0.15;
    const size = bg.size ?? 'cover';
    const position = bg.position ?? 'center';

    if (bg.image) {
      // CSS ::before pseudo-element for image overlay — never touches React DOM
      cssRules.push(
        `${selector} { position: relative !important; isolation: isolate; overflow: hidden; }`,
        `${selector}::before {`,
        `  content: '';`,
        `  position: absolute;`,
        `  inset: 0;`,
        `  background-image: url('${bg.image}');`,
        `  background-size: ${size};`,
        `  background-position: ${position};`,
        `  background-repeat: no-repeat;`,
        `  opacity: ${opacity};`,
        `  z-index: -1;`,
        `  pointer-events: none;`,
        `}`
      );
    }

    if (bg.video) {
      // Video injection — deferred so React has painted first
      requestAnimationFrame(() => {
        document.querySelectorAll<HTMLElement>(selector).forEach(el => {
          if (el.querySelector('[data-theme-video-bg]')) return; // avoid duplicates
          const computed = getComputedStyle(el);
          if (computed.position === 'static') el.style.position = 'relative';
          el.style.overflow = 'hidden';

          const video = document.createElement('video');
          video.setAttribute('data-theme-video-bg', 'true');
          video.autoplay = true;
          video.loop = true;
          video.muted = true;
          video.playsInline = true;
          video.style.cssText = [
            'position:absolute', 'inset:0', 'width:100%', 'height:100%',
            'object-fit:cover', 'pointer-events:none', 'z-index:-1',
            `opacity:${opacity}`,
          ].join(';');
          video.src = bg.video!;
          el.insertBefore(video, el.firstChild);
        });
      });
    }
  });

  if (cssRules.length > 0) {
    const styleEl = document.createElement('style');
    styleEl.id = 'theme-bg-styles';
    styleEl.textContent = cssRules.join('\n');
    document.head.appendChild(styleEl);
  }
}

// Load a font dynamically
export async function loadFont(cssFamily: string): Promise<void> {
  if (typeof document === 'undefined') return;

  // Parse font family name (remove quotes and get first font)
  const fontName = cssFamily.split(',')[0].replace(/['"]/g, '').trim();

  // Check if it's a Google Font
  const googleFont = GOOGLE_FONTS.find(f => f.family === fontName);

  if (googleFont) {
    // Check if already loaded
    const existingLink = document.querySelector(`link[href*="${fontName.replace(' ', '+')}"]`);
    if (existingLink) return;

    // Load via Google Fonts API
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, '+')}:wght@${googleFont.variants.join(';')}&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Wait for font to load
    await document.fonts.ready;
  }
  // For custom fonts, assume they're already loaded via @font-face
}

// Merge theme with overrides
export function mergeThemeWithOverrides(theme: Theme, overrides: Partial<ThemeTokens>): Theme {
  return {
    ...theme,
    tokens: {
      colors: { ...(theme.tokens?.colors || {}), ...(overrides.colors || {}) },
      typography: { ...(theme.tokens?.typography || {}), ...(overrides.typography || {}) },
      spacing: { ...(theme.tokens?.spacing || {}), ...(overrides.spacing || {}) },
      radius: { ...(theme.tokens?.radius || {}), ...(overrides.radius || {}) },
      shadows: { ...(theme.tokens?.shadows || {}), ...(overrides.shadows || {}) },
      backgrounds: { ...(theme.tokens?.backgrounds || {}), ...(overrides.backgrounds || {}) },
    },
  };
}

// Get page key from pathname
export function getPageKeyFromPathname(pathname: string): string | null {
  // Remove leading slash and get first segment
  const segments = pathname.replace(/^\//, '').split('/');
  const pageKey = segments[0];

  // Valid page keys
  const validKeys = ['home', 'marketplace', 'profile', 'feed', 'events', 'stats', 'search', 'media', 'music-coach', 'optimizer', 'synchronization'];

  return validKeys.includes(pageKey) ? pageKey : null;
}

// Color conversion utilities
export function hslToHex(hsl: string): string {
  // Parse HSL string "240 5% 96%"
  const parts = hsl.trim().split(/\s+/);
  if (parts.length !== 3) return '#000000';

  const h = parseInt(parts[0]);
  const s = parseInt(parts[1].replace('%', '')) / 100;
  const l = parseInt(parts[2].replace('%', '')) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;

  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToHsl(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (diff !== 0) {
    s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / diff + (g < b ? 6 : 0)) * 60;
        break;
      case g:
        h = ((b - r) / diff + 2) * 60;
        break;
      case b:
        h = ((r - g) / diff + 4) * 60;
        break;
    }
  }

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
